import type { ZodType } from 'zod';

import type { CustomFieldError, CustomFieldErrors } from './FormValidationError';
import { get, isValidError, set } from './ErrorsService';

function getIssuePath(path: ReadonlyArray<string | number>): string {
    return path.map(String).join('.');
}

const setError = (
    errors: CustomFieldErrors,
    path: string,
    error: CustomFieldError,
): CustomFieldErrors => {
    set(errors, `${path}.type`, error.type);
    set(errors, `${path}.message`, error.message);

    return errors;
};

function getMergedErrors(root: CustomFieldErrors | CustomFieldError): CustomFieldErrors {
    return Object.keys(root).length
        ? {
              root,
          }
        : {};
}

export const zodValidationResolver =
    <FormValues>(validationSchema: ZodType<FormValues>) =>
    async (
        data: unknown,
        _context?: unknown,
        { root = {} }: CustomFieldErrors = {},
    ): Promise<{ errors: CustomFieldErrors; values: unknown }> => {
        const result = await validationSchema.safeParseAsync(data);

        if (result.success) {
            return {
                values: result.data,
                errors: getMergedErrors(root),
            };
        }

        const mappedErrors = result.error.issues.reduce(
            (allErrors, issue) => {
                const path = getIssuePath(issue.path as Array<string | number>);
                const errorPath = path || '';
                const existingError = get(allErrors, errorPath) as unknown as CustomFieldError;

                // keep first validation error per path (same as yupValidationResolver)
                if (isValidError(existingError)) {
                    return allErrors;
                }

                setError(allErrors, errorPath, {
                    type: issue.code ?? 'validation',
                    message: issue.message,
                });

                return allErrors;
            },
            getMergedErrors(root) as CustomFieldErrors,
        );

        return {
            values: {},
            errors: mappedErrors,
        };
    };
